import { Box, Skeleton, Typography } from "@mui/material";
import { toLocaleDateTimeString } from "@utils";
import {
  OrderOut,
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1ListAttachmentsQuery,
  useWebApiV1ListWalkthroughAttachmentsQuery,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query";
import DVHCard from "@pages/PhysicianReview/components/PhysicianReviewContentCards/DVHCard";
import ImageCard from "@pages/PhysicianReview/components/PhysicianReviewContentCards/ImageCard";
import PlanWalkthroughCard from "@pages/PhysicianReview/components/PhysicianReviewContentCards/PlanWalkthroughCard";
import ScorecardCard from "@pages/PhysicianReview/components/PhysicianReviewContentCards/ScorecardCard";
import { useMemo } from "react";
import { Feature } from "@enums";

const PhysicianReviewSkeleton = () => {
  return (
    <>
      <Skeleton variant="rectangular" height={32} width={120} />
      <Skeleton variant="rectangular" height={80} />
      <Skeleton variant="rectangular" height={268} />
      <Skeleton variant="rectangular" height={268} />
      <Skeleton variant="rectangular" height={268} />
    </>
  );
};

type Props = {
  order?: OrderOut;
};

const PhysicianReviewContent = ({ order }: Props) => {
  const orderId = order?.id;

  const {
    data: scorecardAttachmentsData,
    isLoading: scorecardAttachmentsLoading,
  } = useWebApiV1ListAttachmentsQuery(
    orderId !== undefined
      ? {
          orderId,
        }
      : skipToken,
  );

  const scorecardsAvailable =
    !!scorecardAttachmentsData?.scorecards &&
    scorecardAttachmentsData.scorecards.length > 0;

  const {
    data: walkthroughAttachmentsData,
    isLoading: walkthroughAttachmentsLoading,
  } = useWebApiV1ListWalkthroughAttachmentsQuery(
    orderId !== undefined
      ? {
          orderId,
        }
      : skipToken,
  );

  const { walkthroughs, images } = useMemo(() => {
    if (!walkthroughAttachmentsData) {
      return { walkthroughs: [], images: [] };
    }

    const walkthroughs = walkthroughAttachmentsData.filter(
      (w) => w.fileName.search(/\.(mp4|mov|webm)$/i) !== -1,
    );

    const images = walkthroughAttachmentsData?.filter(
      (w) => w.fileName.search(/\.(jpg|jpeg|png|webp)$/i) !== -1,
    );

    return { walkthroughs, images };
  }, [walkthroughAttachmentsData]);

  const walkthroughsAvailable = walkthroughs && walkthroughs.length > 0;

  const imagesAvailable = images && images.length > 0;

  const scorecardData =
    scorecardAttachmentsData?.scorecards?.[0]?.scorecardObj ?? null;
  const dvhData =
    scorecardAttachmentsData?.scorecards?.[0]?.scorecardObj?.DVHData ?? null;

  const featureSwitchData = useWebApiV1GetUserFeatureSwitchesQuery();

  const showDvh = useMemo(
    () =>
      featureSwitchData.data?.find(
        (feature) => feature.name === Feature.SHOW_DVH,
      ),
    [featureSwitchData],
  );

  // Use the sentForReviewDatetime from the order object
  const readyForApprovalDatetime = order?.sentForReviewDatetime;

  if (!order) {
    return <PhysicianReviewSkeleton />;
  }

  return (
    <>
      <Typography
        variant="body1"
        data-testid="submitted-datetime"
        sx={{
          color: "text.primary",
        }}
      >
        Submitted:{" "}
        {readyForApprovalDatetime
          ? toLocaleDateTimeString(readyForApprovalDatetime)
          : "-"}
      </Typography>
      {order.planReviewSummary && (
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              display: "inline",
            }}
          >
            Summary:{" "}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              display: "inline",
            }}
          >
            {order.planReviewSummary}
          </Typography>
        </Box>
      )}
      <ScorecardCard
        scorecardAttachmentsLoading={scorecardAttachmentsLoading}
        scorecardsAvailable={scorecardsAvailable}
        scorecardData={scorecardData}
      />
      {showDvh && (
        <DVHCard
          scorecardAttachmentsLoading={scorecardAttachmentsLoading}
          dvhData={dvhData}
        />
      )}
      <PlanWalkthroughCard
        walkthroughAttachmentsLoading={walkthroughAttachmentsLoading}
        walkthroughsAvailable={walkthroughsAvailable}
        walkthroughs={walkthroughs}
      />
      <ImageCard
        walkthroughAttachmentsLoading={walkthroughAttachmentsLoading}
        imagesAvailable={imagesAvailable}
        images={images}
      />
    </>
  );
};

export default PhysicianReviewContent;
